import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/data/status-badge";
import type { RomaneioPublicoDTO } from "@shared/mercado";
import { useCotacaoHoje } from "@/hooks/use-cotacoes";
import { FormConferencia } from "@/components/mercado/form-conferencia";
import { useConferirPublico } from "@/hooks/use-conferencia";

/**
 * O link que o corretor abre. FORA do gate de autenticação (ver App.tsx): quem abre
 * não tem conta no Rizoma — conta de corretor é o C2.
 *
 * Mobile-first e sem menu de propósito: o uso real é o corretor abrindo no celular,
 * no pátio, para conferir peso e total. Nada aqui navega para dentro do app.
 */

function fmtMoeda(v: number) {
  return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtKg(v: number) {
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}
function fmtData(iso: string) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

const STATUS_CARGA_LABEL: Record<string, string> = {
  ENVIADO: "Enviado",
  VISTO: "Visto",
  CONFERIDO: "Conferido",
  // C3 — conferido COM diferença. Palavra própria de propósito: "conferido" e
  // "conferido, mas veio 80 kg a menos" levam a conversas diferentes.
  AJUSTADO: "Ajustado",
};
const STATUS_PAGAMENTO_LABEL: Record<string, string> = {
  A_PAGAR: "A pagar",
  PAGO: "Pago",
};

export default function RomaneioPublicoPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const conferir = useConferirPublico(token);

  const { data, isLoading, isError } = useQuery<RomaneioPublicoDTO>({
    queryKey: ["/api/mercado/publico", token],
    queryFn: () => fetchJson<RomaneioPublicoDTO>(`/api/mercado/publico/${token}`),
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-sm text-muted-foreground">
        Abrindo a nota…
      </div>
    );
  }

  // Revogado, expirado e inexistente chegam aqui como o MESMO 404 — a tela não tenta
  // adivinhar qual dos três foi, porque o servidor de propósito não conta.
  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-bold">Este link não está mais disponível.</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Peça um link novo para quem enviou o romaneio.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-md p-4 space-y-4">
        <header className="rounded-xl border bg-background p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Romaneio</p>
          <p className="text-xl font-extrabold">{data.numero}</p>
          <p className="text-sm font-semibold mt-1">{data.produtor.nome}</p>
          {data.produtor.cidade && (
            <p className="text-xs text-muted-foreground">{data.produtor.cidade}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <StatusBadge tom="info" ponto>{STATUS_CARGA_LABEL[data.statusCarga] ?? data.statusCarga}</StatusBadge>
            <StatusBadge tom={data.statusPagamento === "PAGO" ? "sucesso" : "atencao"} ponto>
              {STATUS_PAGAMENTO_LABEL[data.statusPagamento] ?? data.statusPagamento}
            </StatusBadge>
          </div>
        </header>

        <section className="rounded-xl border bg-background overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted text-muted-foreground uppercase text-[10px]">
                <th className="text-left p-2">Produto</th>
                <th className="text-right p-2">Kg</th>
                <th className="text-right p-2">R$/kg</th>
                <th className="text-right p-2">Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.itens.map((it, i) => (
                <tr key={`${it.variedade}-${it.qualidade ?? ""}-${i}`} className="border-t">
                  <td className="p-2 font-medium">
                    {it.variedade}{it.qualidade ? ` ${it.qualidade}` : ""}
                  </td>
                  <td className="p-2 text-right">{fmtKg(it.kg)}</td>
                  <td className="p-2 text-right">{it.precoKg.toFixed(2).replace(".", ",")}</td>
                  <td className="p-2 text-right font-bold">{fmtMoeda(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <LinhaPracaDaVenda dataEmissao={data.dataEmissao} />

        {/* O motivo de o C3 existir: o comprador confere SEM ter conta. Some em
            venda que não aceita conferência (cancelada) — `podeConferir` vem do
            servidor, a tela não decide isso sozinha. */}
        {data.podeConferir && (
          <FormConferencia
            itens={data.itens.map(i => ({ variedade: i.variedade, qualidade: i.qualidade, kg: i.kg, precoKg: i.precoKg }))}
            conferencia={data.conferencia}
            onConferir={dados => conferir.mutate(dados)}
            salvando={conferir.isPending}
            erro={conferir.error ? (conferir.error as Error).message : null}
          />
        )}

        <section className="rounded-xl border bg-background p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total</span>
            <span className="font-semibold">{fmtKg(data.totalKg)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-muted-foreground text-sm">Valor</span>
            <span className="text-2xl font-extrabold">{fmtMoeda(data.totalValor)}</span>
          </div>
          <div className="flex justify-between text-sm border-t pt-2">
            <span className="text-muted-foreground">Emissão</span>
            <span className="font-semibold">{fmtData(data.dataEmissao)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Vencimento</span>
            <span className="font-semibold">{data.vencimento ? fmtData(data.vencimento) : "—"}</span>
          </div>
        </section>

        {/* C1 mandava para uma página de espera. Com o C2 o acesso existe: este botão
            leva ao cadastro, que já vem pré-preenchido a partir deste mesmo token. */}
        <Button
          variant="outline"
          className="w-full h-12"
          onClick={() => setLocation(`/v/${token}/criar-acesso`)}
          data-testid="button-meus-romaneios"
        >
          Ver todos os meus romaneios
        </Button>

        <footer className="text-[11px] text-muted-foreground text-center pb-6">
          Documento gerado por {data.produtor.nome} no Rizoma. Este link é pessoal.
        </footer>
      </div>
    </div>
  );
}

/**
 * "Preço da praça em 28/08: 1ª R$ 1,45 · 2ª 0,85 · 3ª 0,30" — a referência abaixo
 * dos itens.
 *
 * ⚠️ A cotação é a da DATA DA VENDA (a última publicação com `data ≤ emissão`),
 * **nunca a de hoje**. Uma nota de junho aberta em setembro tem de mostrar o que
 * a praça pagava em junho: com o preço de hoje ao lado de um preço fechado três
 * meses atrás, a linha viraria uma acusação de que o produtor cobrou a mais.
 *
 * Sem cotação naquela data, a linha simplesmente não aparece.
 */
function LinhaPracaDaVenda({ dataEmissao }: { dataEmissao: string }) {
  const { data } = useCotacaoHoje(undefined, dataEmissao);
  if (!data || data.linhas.length === 0) return null;
  return (
    <p className="px-1 text-xs text-muted-foreground" data-testid="linha-praca-publica">
      Preço da praça em {fmtData(data.data ?? dataEmissao).slice(0, 5)}:{" "}
      {data.linhas.map((l, i) => (
        <span key={`${l.variedade}-${l.qualidade ?? ""}`} className="whitespace-nowrap">
          {i > 0 && " · "}
          {l.label.indexOf("Prata ") === 0 ? l.label.slice(6) : l.label}{" "}
          <b className="text-foreground">{i === 0 ? fmtMoeda(l.precoKg) : l.precoKg.toFixed(2).replace(".", ",")}</b>
        </span>
      ))}
    </p>
  );
}
